import { UseQueryResult } from '@tanstack/react-query';
import React from 'react';
import useNotify from './useNotify';

export type useQueryCallbacksArgs<TReturnType> = {
    query: UseQueryResult<TReturnType>;
    onSuccess?: (data: TReturnType) => void;
    onError?: () => void;
};

export default function useQueryCallbacks<TReturnType>({
    query,
    onSuccess,
    onError,
}: useQueryCallbacksArgs<TReturnType>): void {
    React.useEffect(() => {
        if (query.error) {
            onError?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query.error]);

    React.useEffect(() => {
        if (query.isSuccess && !query.isFetching) {
            onSuccess?.(query.data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query.isSuccess, query.isFetching]);
}

export type UseQueryNotifyArgs<TReturnType> = {
    query: UseQueryResult<TReturnType>;
    successMessage?: string;
    errorMessage?: string;
};

export function useQueryNotify<TReturnType>({
    query,
    successMessage,
    errorMessage,
}: UseQueryNotifyArgs<TReturnType>): void {
    const notify = useNotify();

    useQueryCallbacks({
        query,
        onSuccess: () => {
            successMessage && notify('success', successMessage);
        },
        onError: () => {
            errorMessage && notify('error', errorMessage);
        },
    });
}

export function useQueryErrorNotify<TReturnType>(query: UseQueryResult<TReturnType>, errorMessage: string): void {
    useQueryNotify({
        query,
        errorMessage,
    });
}

export function useQuerySuccessNotify<TReturnType>(query: UseQueryResult<TReturnType>, successMessage: string): void {
    useQueryNotify({
        query,
        successMessage,
    });
}
